import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Check, ArrowUpRight } from 'lucide-react';
import { useEnquiry } from '../context/EnquiryContext';
import { FORMSUBMIT_URL, ENQUIRY_PROJECTS, PROJECTS } from '../data/site';

const inputCls =
  'w-full bg-transparent border border-clay px-4 py-3 text-sm text-ink outline-none rounded-none placeholder:text-ink/35 focus:border-terra transition-colors duration-200';

export const EnquiryFormFields = ({ form, setForm, status, onSubmit, submitTestid, formTestid }) => (
  <form onSubmit={onSubmit} className="space-y-5" data-testid={formTestid}>
    <div>
      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">NAME *</label>
      <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="enquiry-input-name" className={inputCls} />
    </div>
    <div>
      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">PHONE NUMBER *</label>
      <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="enquiry-input-phone" className={inputCls} />
    </div>
    <div>
      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">EMAIL</label>
      <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enquiry-input-email" className={inputCls} />
    </div>
    <div>
      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">PROJECT INTERESTED IN *</label>
      <select required value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} data-testid="enquiry-select-project" className={inputCls}>
        <option value="" disabled>Select a project</option>
        {ENQUIRY_PROJECTS.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
    <div>
      <label className="block text-[10px] tracking-[0.22em] text-ink/50 font-medium mb-2">MESSAGE</label>
      <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="enquiry-input-message" className={`${inputCls} resize-none`} />
    </div>
    {status === 'error' && (
      <p className="text-xs text-terra" data-testid="enquiry-error">Something went wrong. Please email prakrutihomers@gmail.com directly.</p>
    )}
    <button type="submit" disabled={status === 'sending'} data-testid={submitTestid}
      className="w-full bg-moss text-sand text-[11px] font-medium tracking-[0.22em] py-4 flex items-center justify-center gap-2 hover:bg-terra transition-colors duration-300 disabled:opacity-60">
      {status === 'sending' ? 'SENDING…' : 'SEND ENQUIRY'} <ArrowUpRight size={14} />
    </button>
  </form>
);

export const useEnquiryForm = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', project: '', message: '' });
  const [status, setStatus] = useState('idle');

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(FORMSUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ _subject: `New Enquiry — ${form.project || 'Prakruti Website'}`, ...form }),
      });
      setStatus(res.ok ? 'sent' : 'error');
    } catch (_) {
      setStatus('error');
    }
  };

  return { form, setForm, status, setStatus, submit };
};

export const EnquirySuccess = () => (
  <div className="px-8 py-16 text-center" data-testid="enquiry-success">
    <span className="w-14 h-14 bg-moss text-sand flex items-center justify-center mx-auto mb-6"><Check size={24} /></span>
    <h4 className="font-heading font-bold text-xl tracking-tight text-ink mb-2">Enquiry sent</h4>
    <p className="text-sm text-ink/60">Our team will call you back within one working day.</p>
  </div>
);

export const EnquiryPopup = () => {
  const { isOpen, close, prefillType } = useEnquiry();
  const { form, setForm, status, setStatus, submit } = useEnquiryForm();

  useEffect(() => {
    if (isOpen) {
      const match = PROJECTS.find((p) => p.name === prefillType);
      setForm((f) => ({ ...f, project: match ? match.name : '' }));
      setStatus('idle');
    }
  }, [isOpen, prefillType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="enquiry-popup"
        >
          <div className="absolute inset-0 bg-bark/70 backdrop-blur-sm" onClick={close} data-testid="enquiry-backdrop" />
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
            className="relative bg-sand w-full max-w-lg border border-clay shadow-[0_40px_90px_rgba(28,31,23,0.35)] max-h-[92vh] overflow-y-auto"
            data-lenis-prevent
          >
            <div className="flex items-start justify-between px-8 pt-8 pb-6 border-b border-clay">
              <div>
                <p className="text-[10px] tracking-[0.28em] text-terra font-medium mb-2">ENQUIRY</p>
                <h3 className="font-heading font-extrabold text-2xl tracking-tighter text-ink">Tell us about your interest</h3>
              </div>
              <button onClick={close} data-testid="enquiry-close-btn" aria-label="Close"
                className="text-ink/50 hover:text-terra transition-colors duration-200 mt-1"><X size={20} /></button>
            </div>
            {status === 'sent' ? (
              <EnquirySuccess />
            ) : (
              <div className="px-8 py-8">
                <EnquiryFormFields form={form} setForm={setForm} status={status} onSubmit={submit} submitTestid="enquiry-submit-btn" formTestid="enquiry-form" />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const FloatingQuoteButton = () => {
  const { open } = useEnquiry();
  return (
    <motion.button
      onClick={() => open()}
      data-testid="floating-enquiry-btn"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.6, duration: 0.6 }}
      className="fixed bottom-6 right-6 z-[80] bg-terra text-white text-[11px] font-medium tracking-[0.2em] px-6 py-4 shadow-[0_16px_40px_rgba(78,154,60,0.4)] hover:bg-moss transition-colors duration-300 flex items-center gap-2"
    >
      ENQUIRY <ArrowUpRight size={14} />
    </motion.button>
  );
};
